import { ReactNode } from 'react';
import { TeContainer, TeContainerSlim, TeSearch, TeText } from '@topcon/tuicl-react';

interface PageHeaderProps {
  /** Page title, shown at the top-left. */
  title: string;
  /** Optional count rendered next to the title, e.g. `(12)`. */
  itemCount?: number;
  /** Rendered after the title/count, still in the left-aligned group — e.g.
   * a Division quick filter (Customers/Invites, PRD D6). */
  leftAccessory?: ReactNode;
  /** Rendered first in the right-aligned group, before the search field —
   * e.g. the Requests Create/Join switch (Figma 6494:27890). */
  rightAccessory?: ReactNode;
  /** Controlled search value. Omit `onSearchChange` to hide the search field. */
  searchValue?: string;
  /** Search change handler. When omitted, no search field is rendered. */
  onSearchChange?: (value: string) => void;
  /** Placeholder for the search field. */
  searchPlaceholder?: string;
  /** Right-aligned actions slot (buttons, menus, etc.). */
  actions?: ReactNode;
}

/**
 * Presentational page header: title + optional count on the left, search and an
 * actions slot on the right. TUICL-first, no local state — the consumer owns
 * search value and data. Composed by `PageLayout` but exported for standalone
 * use.
 */
function PageHeader({
  title,
  itemCount,
  leftAccessory,
  rightAccessory,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  actions,
}: PageHeaderProps) {
  return (
    <TeContainer
      alignItems="center"
      justifyContent="space-between"
      backgroundColor="background.primary"
      borderRadius={4}
      fill
      height="auto"
    >
      <TeContainerSlim alignItems="center" gap="s">
        <TeContainerSlim alignItems="center" gap="xs">
          <TeText size={20} weight={700}>
            {title}
          </TeText>
          {itemCount !== undefined && (
            <TeText size={14} color="text.secondary">
              {`( ${itemCount} )`}
            </TeText>
          )}
        </TeContainerSlim>
        {leftAccessory}
      </TeContainerSlim>

      <TeContainerSlim alignItems="center" gap="s">
        {rightAccessory}
        {onSearchChange && (
          <TeContainerSlim width="256px">
            <TeSearch
              grow
              size="s"
              placeholder={searchPlaceholder}
              value={searchValue}
              clearable={!!searchValue}
              techange={(e) => onSearchChange(e.currentTarget.value)}
            />
          </TeContainerSlim>
        )}
        {actions}
      </TeContainerSlim>
    </TeContainer>
  );
}

export default PageHeader;
export type { PageHeaderProps };
